<template>
  <main class="page">
    <div class="page__main-pane main-pane">
      <header class="header">
        <select v-model="selectedPosition" class="header__select">
          <option
            v-for="(label, key) in positionLabels"
            :key="key"
            :name="key"
            :value="key"
          >
            {{ label }}
          </option>
        </select>
        <button
          type="button"
          class="header__save"
          :disabled="!isSubmitPriorityButtonEnabled"
          @click="saveAdPostPriorities"
        >
          優先度を保存する
        </button>
      </header>
      <section class="main-pane__ruleset-list">
        <AdPostRuleSetList
          :adPosts="editingAdPostListByPosition"
          :selected="selectedAdPostId"
          @select-ruleset="updateSelectedAdPost"
          @reordered-ad-posts="reorderAdPosts"
        />
      </section>
    </div>
    <div class="page__side-pane side-pane">
      <AdPostEditor
        :initialAdPost="selectedAdPost"
        :position="selectedPosition"
        :key="editorKey"
        class="side-pane__editor"
        @update-ad-post="updateAdPost"
        @delete-ad-post="deleteAdPost"
      />
    </div>
  </main>
</template>

<script>
import deepEqual from 'deep-equal';
import deepCopy from 'deepcopy';
import { POSITIONS, POSITION_LABELS } from './constants';
import AdPostRuleSetList from './AdPostRuleSetList/AdPostRuleSetList.vue';
import AdPostEditor from './AdPostEditor/AdPostEditor.vue';
import { restClient } from '../../libs/restClient';

export default {
  name: 'AdPostPage',
  components: {
    AdPostRuleSetList,
    AdPostEditor,
  },
  props: {
    initialAdPosts: {
      type: Array,
      required: true,
    },
  },
  data() {
    const initialSelectedPosition = Object.values(POSITIONS)[0];

    return {
      // 配列をidをキーにした辞書に変換
      savedAdPosts: this.initialAdPosts.reduce((acc, adPost) => {
        return {
          ...acc,
          [adPost.id]: deepCopy(adPost),
        };
      }, {}),
      // 配列をidをキーにした辞書に変換
      editingAdPosts: this.initialAdPosts.reduce((acc, adPost) => {
        return {
          ...acc,
          [adPost.id]: deepCopy(adPost),
        };
      }, {}),
      selectedPosition: initialSelectedPosition,
      selectedAdPostId: undefined,
    };
  },
  computed: {
    editorKey() {
      return this.selectedAdPostId === undefined
        ? 'editor-ad-post-create'
        : `editor-ad-post-${this.selectedAdPostId}`;
    },
    editingAdPostListByPosition() {
      return Object.values(this.editingAdPosts)
        .filter((adPost) => adPost.position === this.selectedPosition)
        .sort((a, b) => a.priority - b.priority);
    },
    positionLabels() {
      return POSITION_LABELS;
    },
    selectedAdPost() {
      return this.editingAdPosts[this.selectedAdPostId];
    },
    isSubmitPriorityButtonEnabled() {
      const savedOrderList = Object.values(this.savedAdPosts)
        .filter((adPost) => adPost.position === this.selectedPosition)
        .sort((a, b) => a.priority - b.priority)
        .map((adPost) => adPost.id);

      const editingOrderList = this.editingAdPostListByPosition.map(
        (adPost) => adPost.id
      );

      return !deepEqual(savedOrderList, editingOrderList);
    },
  },
  watch: {
    selectedPosition() {
      this.selectedAdPostId = undefined;
    },
  },
  methods: {
    async deleteAdPost({ id }) {
      this.editingAdPosts = Object.fromEntries(
        Object.entries(this.editingAdPosts).filter(
          ([adPostId, _]) => adPostId !== id.toString()
        )
      );
      this.savedAdPosts = Object.fromEntries(
        Object.entries(this.savedAdPosts).filter(
          ([adPostId, _]) => adPostId !== id.toString()
        )
      );
      this.selectedAdPostId = undefined;
    },
    async updateAdPost({ adPost, isCreate }) {
      this.editingAdPosts = {
        ...this.editingAdPosts,
        [adPost.id]: deepCopy(adPost),
      };
      this.savedAdPosts = {
        ...this.savedAdPosts,
        [adPost.id]: deepCopy(adPost),
      };

      if (isCreate) {
        this.selectedAdPostId = adPost.id;
      }
    },
    async saveAdPostPriorities() {
      const priorities = this.editingAdPostListByPosition.reduce(
        (acc, adPost) => {
          return {
            ...acc,
            [adPost.id]: adPost.priority,
          };
        },
        {}
      );
      try {
        this.$root.$refs.loadModal.loading = true;
        await restClient.post('/admins/api/ad_posts/priorities', {
          position: this.selectedPosition,
          priorities,
        });

        Object.entries(priorities).forEach(([ad_post_id, priority]) => {
          this.savedAdPosts[ad_post_id].priority = priority;
        });
        this.$root.$refs.loadModal.success = true;
      } catch (error) {
        console.error(error);
        this.$root.$refs.loadModal.error = true;
      } finally {
        setTimeout(() => {
          this.$root.$refs.loadModal.loading = false;
          this.$root.$refs.loadModal.success = false;
          this.$root.$refs.loadModal.error = false;
        }, 1000);
      }
    },
    reorderAdPosts(adPostPriorities) {
      adPostPriorities.forEach(({ adPostId, priority }) => {
        this.editingAdPosts[adPostId].priority = priority;
      });
    },
    updateSelectedAdPost(id) {
      this.selectedAdPostId = id;
    },
  },
};
</script>

<style lang="scss" scoped>
.page {
  display: flex;

  &__main-pane {
    flex: 0 0 600px;
  }

  &__side-pane {
    flex: 1 0 0;

    &:nth-child(n + 2) {
      margin-left: 20px;
    }
  }
}

.header {
  display: flex;
  align-items: center;

  &__select {
    flex: 0 0 auto;
  }

  &__save {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    border: 0;
    border-radius: 4px;
    padding: 0.4em 0.6em 0.3em;
    background-color: #48c78e;
    color: #fff;
    font-size: 14px;
    line-height: 1;

    &:hover {
      cursor: pointer;
    }

    &[disabled] {
      background-color: #eee;
      cursor: not-allowed;
    }

    &:nth-child(n + 2) {
      margin-left: 12px;
    }
  }
}

.main-pane {
  &__ruleset-list {
    &:nth-child(n + 2) {
      margin-top: 20px;
    }
  }
}

.side-pane {
  &__editor {
    position: sticky;
    top: 16px;
  }
}
</style>
